import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import { useDeleteRoomData, usePutRoomData } from "@/api/queries/workplace"
import { useConfirm } from "@/components/app/confirm-dialog"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { formatNumber } from "@/lib/format"
import { entryIssue, MAX_KEY_CHARS, MAX_KEYS, MAX_TOTAL_VALUE_CHARS, MAX_VALUE_CHARS } from "./room-data"

/** Editor state: `null` closed, `{}` new entry, `{ key }` edit that entry. */
type Editor = null | { key?: string }

/** The room's shared data: browse, add, edit and delete entries (one key per write). */
export function RoomDataSection({ room, onOpenFiles }: { room: RoomDetail; onOpenFiles: () => void }) {
  const { t } = useTranslation()
  const remove = useDeleteRoomData(room.id)
  const confirm = useConfirm()
  const [editor, setEditor] = React.useState<Editor>(null)
  const keys = Object.keys(room.room_data).sort()

  const onDelete = (key: string) =>
    void confirm({
      title: t("workplace.room.data.deleteTitle"),
      description: t("workplace.room.data.deleteDescription", { key }),
      confirmLabel: t("workplace.room.data.deleteConfirm"),
      destructive: true,
      onConfirm: () =>
        remove.mutateAsync(key).then(() => void toast.success(t("workplace.room.data.deleted", { key }))),
    })

  return (
    <section className="space-y-2">
      <div className="flex min-h-7 items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-[0.6875rem] font-medium tracking-wide uppercase">
          {t("workplace.room.search.dataTitle", { count: keys.length })}
        </h3>
        <Button size="xs" variant="outline" onClick={() => setEditor({})} data-testid="room-data-add">
          <Plus />
          {t("workplace.room.data.add")}
        </Button>
      </div>
      {keys.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {t("workplace.room.search.dataEmpty")}{" "}
          <button
            type="button"
            className="text-primary underline-offset-4 hover:underline"
            onClick={onOpenFiles}
          >
            {t("workplace.room.search.browseFiles")}
          </button>
        </p>
      ) : (
        <ul className="space-y-1.5">
          {keys.map((k) => (
            <li key={k} className="rounded-lg border px-3 py-1.5" data-testid="room-data-entry">
              <Collapsible>
                <div className="flex items-center gap-1">
                  <CollapsibleTrigger className="group/key flex min-w-0 flex-1 items-center gap-1.5 text-left">
                    <ChevronRight className="text-muted-foreground size-3.5 shrink-0 transition-transform group-data-[panel-open]/key:rotate-90" />
                    <span className="min-w-0 flex-1 truncate font-mono text-xs">{k}</span>
                    <span className="text-muted-foreground shrink-0 text-[0.6875rem] tabular-nums">
                      {t("workplace.room.search.dataMeta", {
                        chars: formatNumber(room.room_data[k]?.length ?? 0),
                      })}
                    </span>
                  </CollapsibleTrigger>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground"
                    aria-label={t("workplace.room.data.edit", { key: k })}
                    onClick={() => setEditor({ key: k })}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={t("workplace.room.data.delete", { key: k })}
                    onClick={() => onDelete(k)}
                  >
                    <Trash2 />
                  </Button>
                </div>
                <CollapsibleContent>
                  <pre className="bg-muted/30 mt-1.5 max-h-60 overflow-auto rounded-md p-2 font-mono text-[0.6875rem] break-words whitespace-pre-wrap">
                    {room.room_data[k]}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            </li>
          ))}
        </ul>
      )}
      <EntryEditor room={room} editor={editor} onClose={() => setEditor(null)} />
    </section>
  )
}

function EntryEditor({ room, editor, onClose }: { room: RoomDetail; editor: Editor; onClose: () => void }) {
  const { t } = useTranslation()
  const update = usePutRoomData(room.id)
  const [key, setKey] = React.useState("")
  const [value, setValue] = React.useState("")
  const [submitted, setSubmitted] = React.useState(false)
  const original = editor?.key
  const open = editor != null

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on open, not on background refetches
  React.useEffect(() => {
    if (!open) return
    setKey(original ?? "")
    setValue(original != null ? (room.room_data[original] ?? "") : "")
    setSubmitted(false)
  }, [open, original])

  const entry = { key, value, original }
  const issue = entryIssue(room.room_data, entry)
  const dirty =
    original == null ? !!key.trim() : key.trim() !== original || value !== room.room_data[original]
  const keyIssue = issue === "keyRequired" || issue === "keyTooLong" || issue === "keyExists"
  const showIssue = issue && (submitted || issue !== "keyRequired")
  const issueText =
    issue &&
    t(`workplace.room.data.issues.${issue}`, {
      keyMax: formatNumber(MAX_KEY_CHARS),
      valueMax: formatNumber(MAX_VALUE_CHARS),
      totalMax: formatNumber(MAX_TOTAL_VALUE_CHARS),
      keysMax: formatNumber(MAX_KEYS),
    })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    if (issue || !dirty) return
    const saved = key.trim()
    update.mutate(
      { key: saved, value, renameFrom: original != null && original !== saved ? original : undefined },
      {
        onSuccess: () => {
          toast.success(t("workplace.room.data.saved", { key: saved }))
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !update.isPending && onClose()}>
      <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {original == null ? t("workplace.room.data.addTitle") : original}
          </DialogTitle>
          <DialogDescription>{t("workplace.room.data.description")}</DialogDescription>
        </DialogHeader>
        <form id="room-data-form" className="flex min-h-0 flex-1 flex-col gap-3" onSubmit={submit} noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="room-data-key">{t("workplace.room.data.key")}</Label>
            <Input
              id="room-data-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="launch_date"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={(showIssue && keyIssue) || undefined}
              aria-describedby={showIssue && keyIssue ? "room-data-issue" : undefined}
              className="font-mono text-[0.8125rem]"
              autoFocus={original == null}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="room-data-value">{t("workplace.room.data.value")}</Label>
            <Textarea
              id="room-data-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t("workplace.room.data.valuePlaceholder")}
              spellCheck={false}
              aria-invalid={(showIssue && !keyIssue) || undefined}
              aria-describedby={showIssue && !keyIssue ? "room-data-issue" : undefined}
              className="field-sizing-fixed min-h-48 flex-1 resize-y font-mono text-xs"
            />
          </div>
          {showIssue && (
            <p id="room-data-issue" role="alert" className="text-destructive text-xs">
              {issueText}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={update.isPending}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="room-data-form" disabled={!dirty || update.isPending}>
            {update.isPending && <Spinner />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
